"use client"

import { useState, useEffect, useRef } from "react"
import { useSelector, useDispatch } from "react-redux"
import {
  Users,
  UserPlus,
  Search,
  Loader,
  UserMinus,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  UserCheck,
  Mail,
  Phone,
  Calendar,
} from "lucide-react"
import { getClassData } from "./api"
import { postAws } from "../postAws"
import { setClassData } from "../features/teachersubject/teacherSubSlice"
import { useNavigate } from "react-router-dom"

const Students = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const selectedClass = useSelector((state) => state.teacherSub.selectedClass)
  const selectedSubject = useSelector((state) => state.teacherSub.selectedSubject)
  const classCode = useSelector((state) => state.teacherSub.classCode)
  const classData = useSelector((state) => state.teacherSub.classData)

  const [searchTerm, setSearchTerm] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [activeTab, setActiveTab] = useState("enrolled")
  const [flashMessage, setFlashMessage] = useState(null)
  const [studentToRemove, setStudentToRemove] = useState(null)
  const [sortConfig, setSortConfig] = useState({ key: "studentName", direction: "ascending" })
  const [copiedCode, setCopiedCode] = useState(false)
  const flashMessageRef = useRef(null)

  const fetchClassData = async () => {
    setLoading(true)
    try {
      const classRoomId = localStorage.getItem("classRoomId")
      if (!classRoomId) {
        throw new Error("ClassRoom ID not found in localStorage")
      }
      const response = await getClassData(classRoomId)
      console.log("API Response:", response)
      if (response.statusCode !== 200 || !response.body || !response.body.data) {
        throw new Error("Invalid response from class API")
      }
      dispatch(setClassData(response.body.data))
      setError(null)
    } catch (err) {
      console.error("Error fetching class data:", err)
      setError("Failed to fetch class data. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchClassData()
  }, [])

  useEffect(() => {
    if (flashMessage) {
      const timer = setTimeout(() => {
        setFlashMessage(null)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [flashMessage])

  useEffect(() => {
    if (copiedCode) {
      const timer = setTimeout(() => {
        setCopiedCode(false)
      }, 2000)
      return () => clearTimeout(timer)
    }
  }, [copiedCode])

  const handleTabChange = (tab) => {
    setActiveTab(tab)
    fetchClassData()
  }

  const handleSearch = (event) => {
    setSearchTerm(event.target.value)
  }

  const handleApprove = async (enrollmentId, studentId) => {
    setLoading(true)
    const approveData = {
      body: {
        _id: enrollmentId,
        studentId: studentId,
        classCode: classCode,
      },
      request: "approve",
    }

    try {
      await postAws("https://api-dev.aurait.ai/enrollment", "POST", approveData)
      await fetchClassData()
      setFlashMessage({ type: "success", message: "Student approved successfully" })
    } catch (error) {
      console.error("Error approving request:", error)
      setFlashMessage({ type: "error", message: "Failed to approve request. Please try again." })
    } finally {
      setLoading(false)
    }
  }

  const handleDecline = async (enrollmentId, studentId) => {
    setLoading(true)
    const declineData = {
      body: {
        _id: enrollmentId,
        studentId: studentId,
        classCode: classCode,
      },
      request: "decline",
    }

    try {
      await postAws("https://api-dev.aurait.ai/enrollment", "POST", declineData)
      await fetchClassData()
      setFlashMessage({ type: "success", message: "Student request declined successfully" })
    } catch (error) {
      console.error("Error declining request:", error)
      setFlashMessage({ type: "error", message: "Failed to decline request. Please try again." })
    } finally {
      setLoading(false)
    }
  }

  const handleRemoveStudent = async (student) => {
    setStudentToRemove(student)
  }

  const confirmRemoveStudent = async () => {
    if (!studentToRemove) return

    setLoading(true)
    const removeData = {
      body: {
        studentId: studentToRemove.studentId,
        classCode: classCode,
      },
      request: "remove-student",
    }

    try {
      await postAws("https://api-dev.aurait.ai/enrollment", "POST", removeData)
      await fetchClassData()
      setFlashMessage({ type: "success", message: "Student removed successfully" })
    } catch (error) {
      console.error("Error removing student:", error)
      setFlashMessage({ type: "error", message: "Failed to remove student. Please try again." })
    } finally {
      setLoading(false)
      setStudentToRemove(null)
    }
  }

  const cancelRemoveStudent = () => {
    setStudentToRemove(null)
  }

  const handleSort = (key) => {
    let direction = "ascending"
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending"
    }
    setSortConfig({ key, direction })
  }

  const sortedStudents = () => {
    if (!classData?.enrolledStudent) return []

    const filteredStudents = classData.enrolledStudent.filter(
      (student) =>
        student.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        student.studentEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (student.phoneNumber && student.phoneNumber.includes(searchTerm)),
    )

    return [...filteredStudents].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? -1 : 1
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? 1 : -1
      }
      return 0
    })
  }

  const sortedRequests = () => {
    if (!classData?.enrollmentRequest) return []

    const filteredRequests = classData.enrollmentRequest.filter(
      (req) =>
        req.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        req.studentEmail.toLowerCase().includes(searchTerm.toLowerCase()),
    )

    return [...filteredRequests].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? -1 : 1
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === "ascending" ? 1 : -1
      }
      return 0
    })
  }

  const copyClassCode = () => {
    navigator.clipboard.writeText(classCode)
    setCopiedCode(true)
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ChevronDown className="w-4 h-4 opacity-50" />
    }
    return sortConfig.direction === "ascending" ? (
      <ChevronUp className="w-4 h-4 text-white" />
    ) : (
      <ChevronDown className="w-4 h-4 text-white" />
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100">
        <div className="bg-white p-8 rounded-xl shadow-xl max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchClassData}
            className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors duration-300 shadow-md"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 -mt-6">
      {flashMessage && (
        <div
          ref={flashMessageRef}
          className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg z-50 flex items-center space-x-2 transform transition-all duration-500 ease-in-out animate-fade-in ${
            flashMessage.type === "error" ? "bg-red-500" : "bg-green-500"
          } text-white max-w-xs sm:max-w-sm`}
        >
          {flashMessage.type === "error" ? (
            <XCircle className="h-5 w-5 flex-shrink-0" />
          ) : (
            <CheckCircle className="h-5 w-5 flex-shrink-0" />
          )}
          <p>{flashMessage.message}</p>
        </div>
      )}

      <div className="container mx-auto px-2 md:px-4 py-0 max-w-7xl">
        {selectedClass && selectedSubject && (
          <>
            {/* Header Section with Class Info, Search, and Tabs */}
            <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mt-6 md:mt-8">
              <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-3 md:px-4 py-3 md:py-3.5">
                {/* Mobile Layout */}
                <div className="block lg:hidden">
                  {/* Row 1: Class Info and Class Code */}
                  <div className="flex items-center justify-between gap-2 mb-3">
                    <div className="flex-1 min-w-0">
                      <h1 className="text-sm font-bold text-white truncate">
                        {selectedClass} - {selectedSubject.charAt(0).toUpperCase() + selectedSubject.slice(1)}
                      </h1>
                    </div>
                    <div className="flex items-center bg-white/20 backdrop-blur-sm text-white rounded-md px-2 py-1 flex-shrink-0">
                      <p className="text-xs font-medium mr-1">Code:</p>
                      <span className="font-mono text-xs font-bold mr-1">{classCode}</span>
                      <button
                        onClick={copyClassCode}
                        className="p-0.5 bg-white/30 text-white rounded hover:bg-white/40 transition-colors"
                        aria-label="Copy class code"
                      >
                        {copiedCode ? <CheckCircle className="w-3 h-3" /> : <ClipboardIcon className="w-3 h-3" />}
                      </button>
                    </div>
                  </div>

                  {/* Row 2: Tabs and Search */}
                  <div className="flex items-center gap-2">
                    {/* Tabs */}
                    <div className="flex bg-white/20 backdrop-blur-sm rounded-lg overflow-hidden border border-white/30 flex-shrink-0">
                      <button
                        onClick={() => handleTabChange("enrolled")}
                        className={`py-1.5 px-2 text-center font-medium transition-colors duration-200 flex items-center text-xs ${
                          activeTab === "enrolled"
                            ? "bg-white text-indigo-600"
                            : "bg-transparent text-white hover:bg-white/10"
                        }`}
                      >
                        <UserCheck className="w-3 h-3 mr-1" />
                        <span>Enrolled</span>
                      </button>
                      <button
                        onClick={() => handleTabChange("requests")}
                        className={`py-1.5 px-2 text-center font-medium transition-colors duration-200 flex items-center text-xs ${
                          activeTab === "requests"
                            ? "bg-white text-indigo-600"
                            : "bg-transparent text-white hover:bg-white/10"
                        }`}
                      >
                        <UserPlus className="w-3 h-3 mr-1" />
                        <span>Requests</span>
                        {classData?.enrollmentRequest?.length > 0 && (
                          <span className="ml-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                            {classData.enrollmentRequest.length}
                          </span>
                        )}
                      </button>
                    </div>

                    {/* Search */}
                    <div className="relative flex-1">
                      <input
                        type="text"
                        placeholder="Search students"
                        value={searchTerm}
                        onChange={handleSearch}
                        className="w-full px-3 py-1.5 pl-7 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-xs"
                      />
                      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-white/70" size={12} />
                    </div>
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden lg:flex lg:items-center justify-between gap-4">
                  {/* Class Info */}
                  <div className="flex items-center gap-4">
                    <div>
                      <h1 className="text-xl lg:text-2xl font-bold text-white mb-1">
                        {selectedClass} - {selectedSubject.charAt(0).toUpperCase() + selectedSubject.slice(1)}
                      </h1>
                    </div>

                    <div className="flex items-center bg-white/20 backdrop-blur-sm text-white rounded-md px-3 py-1.5">
                      <p className="text-sm font-medium mb-0.5">Class Code : </p>
                      <div>
                        <div className="flex items-center gap-1">
                          <span className="font-mono text-sm font-bold ml-1">{classCode}</span>
                          <button
                            onClick={copyClassCode}
                            className="p-1 bg-white/30 text-white rounded-md hover:bg-white/40 transition-colors"
                            aria-label="Copy class code"
                          >
                            {copiedCode ? <CheckCircle className="w-3 h-3" /> : <ClipboardIcon className="w-3 h-3" />}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Search and Tabs */}
                  <div className="flex items-center gap-4">
                    {/* Search */}
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search students"
                        value={searchTerm}
                        onChange={handleSearch}
                        className="w-64 px-4 py-2 pl-10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-base"
                      />
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" size={14} />
                    </div>

                    {/* Tabs */}
                    <div className="flex bg-white/20 backdrop-blur-sm rounded-lg overflow-hidden border border-white/30">
                      <button
                        onClick={() => handleTabChange("enrolled")}
                        className={`py-2 px-4 text-center font-medium transition-colors duration-200 flex items-center text-sm ${
                          activeTab === "enrolled"
                            ? "bg-white text-indigo-600"
                            : "bg-transparent text-white hover:bg-white/10"
                        }`}
                      >
                        <UserCheck className="w-4 h-4 mr-2" />
                        <span>Enrolled</span>
                      </button>
                      <button
                        onClick={() => handleTabChange("requests")}
                        className={`py-2 px-4 text-center font-medium transition-colors duration-200 flex items-center text-sm ${
                          activeTab === "requests"
                            ? "bg-white text-indigo-600"
                            : "bg-transparent text-white hover:bg-white/10"
                        }`}
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        <span>Requests</span>
                        {classData?.enrollmentRequest?.length > 0 && (
                          <span className="ml-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                            {classData.enrollmentRequest.length}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-3 md:p-6">
                {loading ? (
                  <div className="flex flex-col items-center justify-center py-16">
                    <Loader className="w-10 h-10 text-indigo-600 animate-spin mb-4" />
                    <p className="text-gray-600 font-medium">Loading student data...</p>
                  </div>
                ) : (
                  <>
                    {activeTab === "requests" && (
                      <div className="space-y-4 mb-8 animate-fade-in">
                        {sortedRequests().length > 0 ? (
                          <div className="overflow-x-auto rounded-xl shadow-sm">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gradient-to-r from-indigo-500 to-blue-600">
                                <tr>
                                  <th
                                    className="px-3 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer hover:bg-indigo-600/80 transition-colors"
                                    onClick={() => handleSort("studentName")}
                                  >
                                    <div className="flex items-center">
                                      <span>Name</span>
                                      {getSortIcon("studentName")}
                                    </div>
                                  </th>
                                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer transition-colors hidden md:table-cell">
                                    <div className="flex items-center">
                                      <span>Email</span>
                                    </div>
                                  </th>
                                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer transition-colors hidden lg:table-cell">
                                    <div className="flex items-center">
                                      <span>Request Date</span>
                                    </div>
                                  </th>
                                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider hidden sm:table-cell">
                                    Status
                                  </th>
                                  <th className="px-3 md:px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {sortedRequests().map((req) => (
                                  <tr
                                    key={req.enrollmentId}
                                    className="hover:bg-gray-50 transition-colors duration-200"
                                  >
                                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <div className="flex-shrink-0 h-8 w-8 md:h-10 md:w-10">
                                          <img
                                            className="h-8 w-8 md:h-10 md:w-10 rounded-full object-cover"
                                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(req.studentName)}&background=random&color=fff&size=128`}
                                            alt={req.studentName}
                                          />
                                        </div>
                                        <div className="ml-3 md:ml-4">
                                          <div className="text-sm font-medium text-gray-900">{req.studentName}</div>
                                          <div className="text-xs text-gray-500 md:hidden">{req.studentEmail}</div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-3 md:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                                      <div className="text-sm text-gray-500 flex items-center">
                                        <Mail className="w-4 h-4 mr-2 text-gray-400" />
                                        <span>{req.studentEmail}</span>
                                      </div>
                                    </td>
                                    <td className="px-3 md:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                                      <div className="text-sm text-gray-500 flex items-center">
                                        <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                                        <span>{formatDate(req.requestedAt)}</span>
                                      </div>
                                    </td>
                                    <td className="px-3 md:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                                      <span className="px-2 md:px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800">
                                        Pending
                                      </span>
                                    </td>
                                    <td className="px-3 md:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                      <div className="flex items-center justify-end gap-1 md:gap-2">
                                        <button
                                          onClick={() => handleApprove(req.enrollmentId, req.studentId)}
                                          className="flex items-center justify-center bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition duration-200 p-1.5 md:p-2"
                                          aria-label="Approve"
                                        >
                                          <CheckCircle className="w-4 h-4 md:w-5 md:h-5" />
                                        </button>
                                        <button
                                          onClick={() => handleDecline(req.enrollmentId, req.studentId)}
                                          className="flex items-center justify-center bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition duration-200 p-1.5 md:p-2"
                                          aria-label="Decline"
                                        >
                                          <XCircle className="w-4 h-4 md:w-5 md:h-5" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 text-indigo-600 mb-4">
                              <UserPlus className="w-8 h-8" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No pending join requests</h3>
                            <p className="text-gray-500 max-w-md mx-auto">
                              When students request to join your class, they will appear here for your approval.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {activeTab === "enrolled" && (
                      <div className="space-y-4 animate-fade-in">
                        {sortedStudents().length > 0 ? (
                          <div className="overflow-x-auto rounded-xl shadow-sm">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gradient-to-r from-indigo-500 to-blue-600">
                                <tr>
                                  <th
                                    className="px-3 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer hover:bg-indigo-600/80 transition-colors"
                                    onClick={() => handleSort("studentName")}
                                  >
                                    <div className="flex items-center">
                                      <span>Name</span>
                                      {getSortIcon("studentName")}
                                    </div>
                                  </th>
                                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer transition-colors hidden md:table-cell">
                                    <div className="flex items-center">
                                      <span>Email</span>
                                    </div>
                                  </th>
                                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider cursor-pointer transition-colors hidden lg:table-cell">
                                    <div className="flex items-center">
                                      <span>Phone</span>
                                    </div>
                                  </th>
                                  <th className="px-3 md:px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider hidden sm:table-cell">
                                    Status
                                  </th>
                                  <th className="px-3 md:px-6 py-3 text-right text-xs font-medium text-white uppercase tracking-wider">
                                    Actions
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {sortedStudents().map((student, index) => (
                                  <tr
                                    key={student.studentId}
                                    className="hover:bg-gray-50 transition-colors duration-200"
                                  >
                                    <td className="px-3 md:px-6 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        <div className="flex-shrink-0 h-8 w-8 md:h-10 md:w-10">
                                          <img
                                            className="h-8 w-8 md:h-10 md:w-10 rounded-full object-cover"
                                            src={`https://ui-avatars.com/api/?name=${encodeURIComponent(student.studentName)}&background=random&color=fff&size=128`}
                                            alt={student.studentName}
                                          />
                                        </div>
                                        <div className="ml-3 md:ml-4">
                                          <div
                                            className="text-sm font-medium text-gray-900 cursor-pointer hover:text-indigo-600 transition-colors"
                                            onClick={() => navigate(`/students/details/${student.studentId}`)}
                                          >
                                            {student.studentName}
                                          </div>
                                          <div className="text-xs text-gray-500 md:hidden">{student.studentEmail}</div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-3 md:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                                      <div className="text-sm text-gray-500 flex items-center">
                                        <Mail className="w-4 h-4 mr-2 text-gray-400" />
                                        <span>{student.studentEmail}</span>
                                      </div>
                                    </td>
                                    <td className="px-3 md:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                                      <div className="text-sm text-gray-500 flex items-center">
                                        <Phone className="w-4 h-4 mr-2 text-gray-400" />
                                        <span>{student.phoneNumber || "Not provided"}</span>
                                      </div>
                                    </td>
                                    <td className="px-3 md:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                                      <span className="px-2 md:px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                        Enrolled
                                      </span>
                                    </td>
                                    <td className="px-3 md:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                      <button
                                        onClick={() => handleRemoveStudent(student)}
                                        className="flex items-center justify-center ml-auto bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition duration-200 p-1.5 md:p-2"
                                        aria-label="Remove student"
                                      >
                                        <UserMinus className="w-4 h-4 md:w-5 md:h-5" />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200 shadow-sm">
                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 text-indigo-600 mb-4">
                              <Users className="w-8 h-8" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No students enrolled yet</h3>
                            <p className="text-gray-500 max-w-md mx-auto mb-6">
                              Share your class code with students so they can join your class.
                            </p>
                            <div className="bg-indigo-50 p-4 rounded-lg inline-block border border-indigo-200">
                              <div className="flex items-center gap-3">
                                <span className="font-mono text-xl text-indigo-600">{classCode}</span>
                                <button
                                  onClick={copyClassCode}
                                  className="p-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition-colors"
                                  aria-label="Copy class code"
                                >
                                  {copiedCode ? (
                                    <CheckCircle className="w-5 h-5" />
                                  ) : (
                                    <ClipboardIcon className="w-5 h-5" />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Remove Student Confirmation Modal */}
      {studentToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div
            className="bg-white rounded-xl shadow-2xl max-w-md w-full transform transition-all duration-300 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-red-600 to-pink-600 py-6 px-6 rounded-t-xl">
              <h2 className="text-xl font-bold text-white flex items-center">
                <UserMinus className="w-5 h-5 mr-2" />
                Remove Student
              </h2>
            </div>

            <div className="p-6">
              <div className="flex items-center gap-4 mb-6 bg-red-50 p-4 rounded-lg">
                <img
                  className="h-12 w-12 rounded-full object-cover border-2 border-white shadow-md"
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(studentToRemove.studentName)}&background=random&color=fff&size=128`}
                  alt={studentToRemove.studentName}
                />
                <div>
                  <p className="font-medium text-gray-900">{studentToRemove.studentName}</p>
                  <p className="text-sm text-gray-500">{studentToRemove.studentEmail}</p>
                </div>
              </div>

              <p className="mb-6 text-gray-700">
                Are you sure you want to remove this student from your class? This action cannot be undone.
              </p>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={cancelRemoveStudent}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmRemoveStudent}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-300 flex items-center justify-center shadow-md font-medium"
                >
                  {loading ? (
                    <>
                      <Loader className="animate-spin mr-2" size={16} />
                      Removing...
                    </>
                  ) : (
                    "Remove Student"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Clipboard icon component
const ClipboardIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
  </svg>
)

export default Students
